import moment from 'moment'
import Router from 'next/router'
import * as R from 'ramda'
import React, { useCallback, useEffect, useMemo } from 'react'
import { isInclusivelyAfterDay } from 'react-dates'
import { useFormContext, useWatch } from 'react-hook-form/dist/index.ie11'
import styled, { css } from 'styled-components'

import {
  Checkbox,
  DatePicker,
  Field,
  Hidden,
  TextArea,
  TextInput
} from '../../../components/Form'
import {
  ErrorLabel,
  HelpText,
  OutsideLabel as Label
} from '../../../components/Form/shared'
import { Col, Row } from '../../../components/Layout/FlexGrid'
import { usePrev } from '../../../hooks'
import { useTranslation } from '../../../i18n'
import { getColor, getFontSize } from '../../../utils/cssUtils'
import Dropzone from '../../Form/Dropzone'
import TimeInput from '../../Form/TimeInput'
import { getValidationRules } from './validationRules'

const TextAreaWrapper = styled.div`
  position: relative;
`
const StyledTextArea = styled(TextArea)`
  height: 192px;
`
const DonationOptionsError = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  margin-bottom: 24px;
`
const StyledCheckbox = styled(Checkbox)`
  height: 32px;
`
const Disclaimer = styled.div`
  color: ${getColor('theme')};
  background-color: ${getColor('lightPink')};
  font-size: ${getFontSize('body2')};
  padding: 16px;
  border-radius: 4px;
  > p:last-of-type {
    margin: 0;
  }
`
const TextCounterWrapper = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  padding: 4px;
  line-height: 1;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  color: ${({ exceed }) =>
    exceed
      ? css`
          ${getColor(`error`)}
        `
      : css`
          ${getColor('lightGrey')}
        `};
  ${({ show }) =>
    show &&
    css`
      opacity: 1;
    `}
`
const CorporateCreatorHeadline = styled.div`
  color: ${getColor('black')};
  font-size: ${getFontSize('title')};
  font-weight: bold;
`
const DynamicCol = styled(Col)`
  ${({ show }) =>
    !show &&
    css`
      display: none;
    `}
`

const descriptionTextLimit = 600

const TextCounter = () => {
  const description = useWatch({ name: 'description', defaultValue: '' })

  const textLength = R.length(description)

  const show = textLength > 579

  return (
    <TextCounterWrapper show={show} exceed={textLength > descriptionTextLimit}>
      {textLength}/{descriptionTextLimit}
    </TextCounterWrapper>
  )
}

const CreateFormContent = ({
  defaultValues,
  projectNo,
  projectName,
  fundraisingEventNo,
  isEdit,
  isSavePreviewLoading,
  isSaveDraftLoading,
  endTime,
  onBeforeUnload,
  shouldHandleOnBeforeUnload,
  isPublish,
  ...props
}) => {
  const methods = useFormContext()

  const {
    watch,
    getValues,
    setValue,
    errors,
    trigger,
    formState: { isDirty, dirtyFields }
  } = methods

  const { t } = useTranslation('common')

  const useCorporateCreator = watch('useCorporateCreator')

  const prevUseCorporateCreator = usePrev(useCorporateCreator)

  const projectEndWithinThreeMonths = useMemo(() => {
    const projectEndTime = endTime
    const threeMonthsFromToday = moment().add(3, 'months')
    if (R.isNil(projectEndTime)) {
      return false
    } else {
      const projectEndMoment = moment(projectEndTime)
      return projectEndMoment.isSameOrBefore(threeMonthsFromToday)
    }
  }, [endTime])

  const endMoment = useMemo(
    () =>
      projectEndWithinThreeMonths ? moment(endTime) : moment().add(3, 'months'),
    [projectEndWithinThreeMonths, endTime]
  )

  const getOutsideRange = useCallback(
    day => {
      return (
        !isInclusivelyAfterDay(day, moment().add(1, 'days')) ||
        isInclusivelyAfterDay(day, endMoment)
      )
    },
    [endMoment]
  )

  const getRules = useMemo(
    () => getValidationRules({ methods, isPublish, t, endMoment }),
    [methods, isPublish, endMoment]
  )

  const handleEmailBlur = e => {
    trigger(['contactEmail', 'contactEmailAgain'])
  }

  const handleDescriptionChange = e => {
    trigger('description')
  }

  const handleBeforeUnload = useCallback(
    e => {
      e.preventDefault()
      // for chrome
      e.returnValue = ''
      onBeforeUnload && onBeforeUnload(getValues())
    },
    [onBeforeUnload, getValues]
  )

  const handleRouteChange = useCallback(
    url => {
      onBeforeUnload && onBeforeUnload(getValues())
    },
    [onBeforeUnload, getValues]
  )

  useEffect(() => {
    if (
      isDirty &&
      onBeforeUnload &&
      !R.isEmpty(dirtyFields) &&
      shouldHandleOnBeforeUnload
    ) {
      window.addEventListener('beforeunload', handleBeforeUnload)
      Router.events.on('routeChangeStart', handleRouteChange)
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload)
        Router.events.off('routeChangeStart', handleRouteChange)
      }
    }
  }, [
    defaultValues,
    isDirty,
    getValues,
    onBeforeUnload,
    handleBeforeUnload,
    handleRouteChange,
    dirtyFields,
    shouldHandleOnBeforeUnload
  ])

  useEffect(() => {
    if (prevUseCorporateCreator && !useCorporateCreator) {
      setValue('corporateCreatorName', null)
      setValue('corporateCreatorLogoImage', null)
      setValue('corporateCreatorBR', null)
    }
  }, [useCorporateCreator])

  return (
    <Row gutter={[24, 48]}>
      <Col md={24}>
        {fundraisingEventNo && (
          <Hidden name='fundraisingEventNo' value={fundraisingEventNo} />
        )}
        <Hidden name='projectNo' value={projectNo} />
        <Field
          name='title'
          label={t('personalFundraising.create.form.title.label')}
          helpText={t('personalFundraising.create.form.title.help')}
          validationRules={getRules('title')}
        >
          <TextInput />
        </Field>
      </Col>
      <Col md={24}>
        <Row gutter={[24, 24]}>
          <Col md={12}>
            <Field
              name='contactEmail'
              label={t('personalFundraising.create.form.contactEmail.label')}
              helpText={t('personalFundraising.create.form.contactEmail.help')}
              validationRules={getRules('contactEmail')}
            >
              <TextInput type='email' onBlur={handleEmailBlur} />
            </Field>
          </Col>
          <Col md={12}>
            <Field
              name='contactEmailAgain'
              label={t(
                'personalFundraising.create.form.contactEmailAgain.label'
              )}
              validationRules={getRules('contactEmailAgain')}
            >
              <TextInput type='email' onBlur={handleEmailBlur} />
            </Field>
          </Col>
        </Row>
      </Col>
      <Col>
        <Row gutter={[24, 0]}>
          <Col md={12}>
            <Field
              name='donationTarget'
              label={t('personalFundraising.create.form.donationTarget.label')}
              helpText={t(
                'personalFundraising.create.form.donationTarget.help'
              )}
              validationRules={getRules('donationTarget')}
            >
              <TextInput
                prefix='HKD'
                type='number'
                inputMode='numeric'
                min='10'
                max='1000000'
              />
            </Field>
          </Col>
        </Row>
      </Col>
      <Col md={24}>
        <Row
          gutter={[
            { md: 24, xs: 16 },
            { md: 8, xs: 8 }
          ]}
        >
          <Col md={12} xs={12}>
            <DatePicker
              name='endDate'
              label={t('personalFundraising.create.form.endDate.label')}
              placeholder={t(
                'personalFundraising.create.form.endDate.placeholder'
              )}
              isOutsideRange={getOutsideRange}
              helpText={
                projectEndWithinThreeMonths
                  ? t(
                      'personalFundraising.create.form.endDate.projectEndWithinThreeMonths',
                      {
                        date: moment(endTime).format('YYYY/MM/DD')
                      }
                    )
                  : t('personalFundraising.create.form.endDate.help')
              }
              showClearDate
              validationRules={getRules('endDate')}
            />
          </Col>
          <Col md={12} xs={12}>
            <TimeInput
              name='endTime'
              label={t('personalFundraising.create.form.endTime.label')}
              placeholder={t(
                'personalFundraising.create.form.endTime.placeholder'
              )}
              validationRules={getRules('endTime')}
            />
          </Col>
        </Row>
      </Col>
      <Col>
        <TextAreaWrapper>
          <Field
            name='description'
            label={t('personalFundraising.create.form.description.label')}
            labelOutside
            validationRules={getRules('description')}
          >
            <StyledTextArea
              placeholder={t(
                'personalFundraising.create.form.description.placeholder'
              )}
              maxLength={descriptionTextLimit + 100}
              onChange={handleDescriptionChange}
            />
          </Field>
          <TextCounter />
        </TextAreaWrapper>
      </Col>
      <Col>
        <Row>
          <Col>
            <Label>
              {t(
                'personalFundraising.create.form.donationItemsDraftSetting.label'
              )}
            </Label>
            <Row
              gutter={[
                { md: 24, xs: 16 },
                { md: 8, xs: 8 }
              ]}
            >
              {R.times(
                i => (
                  <Col md={12} xs={12} key={i}>
                    <Field
                      index={i}
                      name={`donationItemsDraftSetting[${i}]`}
                      showError={false}
                      validationRules={getRules('donationItemsDraftSetting')}
                    >
                      <TextInput
                        prefix='HKD'
                        type='number'
                        inputMode='numeric'
                        min='10'
                        max='50000'
                      />
                    </Field>
                  </Col>
                ),
                4
              )}
            </Row>
            <Row>
              <Col>
                <HelpText>
                  {t(
                    'personalFundraising.create.form.donationItemsDraftSetting.help'
                  )}
                </HelpText>
              </Col>
              <Col>
                <DonationOptionsError>
                  <ErrorLabel>
                    {R.pipe(
                      R.paths(
                        R.times(i => ['donationItemsDraftSetting', i], 4)
                      ),
                      R.reject(R.isNil),
                      R.project(['type', 'message']),
                      R.uniq,
                      R.pluck('message'),
                      R.join(t('common.pausePunctuation'))
                    )(errors)}
                  </ErrorLabel>
                </DonationOptionsError>
              </Col>
            </Row>
          </Col>
          <Col>
            <StyledCheckbox
              name='allowCustomDonation'
              label={t(
                'personalFundraising.create.form.allowCustomAmount.label'
              )}
              defaultValue
            />
            <HelpText>
              {t('personalFundraising.create.form.allowCustomAmount.help')}
            </HelpText>
          </Col>
        </Row>
      </Col>
      <Col>
        <Dropzone
          name='titleImage'
          label={t('personalFundraising.create.form.titleImage.label')}
          helpText={t('personalFundraising.create.form.titleImage.help')}
          multiple={false}
          maxUploadCount={1}
          validationRules={getRules('titleImage')}
        />
      </Col>
      <Col>
        <Field
          name='youtubeId'
          label={t('personalFundraising.create.form.youtubeId.label')}
          labelOutside
          validationRules={getRules('youtubeId')}
        >
          <TextInput
            placeholder={t(
              'personalFundraising.create.form.youtubeId.placeholder'
            )}
          />
        </Field>
      </Col>
      <Col>
        <Dropzone
          name='relatedImages'
          label={t('personalFundraising.create.form.relatedImages.label')}
          helpText={t('personalFundraising.create.form.relatedImages.help')}
          maxUploadCount={10}
          validationRules={getRules('relatedImages')}
        />
      </Col>
      <Col>
        <CorporateCreatorHeadline>
          {t('personalFundraising.create.form.corporateCreator.headline')}
        </CorporateCreatorHeadline>
        <StyledCheckbox
          name='useCorporateCreator'
          label={t('personalFundraising.create.form.useCorporateCreator.label')}
          defaultValue
        />
      </Col>
      <DynamicCol show={useCorporateCreator}>
        <Field
          name='corporateCreatorName'
          label={t(
            'personalFundraising.create.form.corporateCreator.name.label'
          )}
          helpText={t(
            'personalFundraising.create.form.corporateCreator.name.help'
          )}
          validationRules={getRules('corporateCreatorName')}
        >
          <TextInput disabled={!useCorporateCreator} />
        </Field>
      </DynamicCol>
      <DynamicCol show={useCorporateCreator}>
        <Dropzone
          name='corporateCreatorLogoImage'
          label={t(
            'personalFundraising.create.form.corporateCreator.logoImage.label'
          )}
          helpText={t(
            'personalFundraising.create.form.corporateCreator.logoImage.help'
          )}
          multiple={false}
          maxUploadCount={1}
          disabled={!useCorporateCreator}
          validationRules={getRules('corporateCreatorLogoImage')}
        />
      </DynamicCol>
      <DynamicCol show={useCorporateCreator}>
        <Dropzone
          name='corporateCreatorBR'
          label={t('personalFundraising.create.form.corporateCreator.BR.label')}
          helpText={t(
            'personalFundraising.create.form.corporateCreator.BR.help'
          )}
          multiple={false}
          maxUploadCount={1}
          disabled={!useCorporateCreator}
          validationRules={getRules('corporateCreatorBR')}
        />
      </DynamicCol>
      <Col>
        <Disclaimer>
          {t('personalFundraising.create.disclaimer', {
            returnObjects: true
          }).map((text, i) => (
            <p key={i}>{text}</p>
          ))}
        </Disclaimer>
      </Col>
    </Row>
  )
}

export default CreateFormContent
